var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defineComponent as _defineComponent } from 'vue';
import { SearchIcon } from "lucide-vue-next";
import { ref, computed, watch, nextTick, onMounted, onUnmounted, useAttrs, } from "vue";
import Checkbox from "../Checkbox.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Select',
    props: {
        modelValue: { type: [String, Number, Array], required: false },
        options: { type: Array, required: true },
        multiple: { type: Boolean, required: false, default: false },
        searchable: { type: Boolean, required: false, default: false },
        clearable: { type: Boolean, required: false, default: false },
        disabled: { type: Boolean, required: false, default: false },
        placeholder: { type: String, required: false, default: "" },
        searchPlaceholder: { type: String, required: false, default: "" },
        clearText: { type: String, required: false, default: "" },
        noOptionsText: { type: String, required: false, default: "" },
        name: { type: String, required: false, default: "" },
        required: { type: Boolean, required: false, default: false },
        tabindex: { type: Number, required: false, default: 0 },
        id: { type: String, required: false, default: "" }
    },
    emits: ["update:modelValue", "change", "focus", "blur", "open", "close", "search", "clear"],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const emit = __emit;
        const attrs = useAttrs();
        // Separate attrs for different elements
        const containerAttrs = computed(() => {
            const { class: _, style: __ } = attrs, rest = __rest(attrs, ["class", "style"]);
            return rest;
        });
        const selectAttrs = computed(() => {
            const allowedAttrs = ["form", "autofocus"];
            return Object.keys(attrs)
                .filter((key) => allowedAttrs.includes(key))
                .reduce((obj, key) => {
                obj[key] = attrs[key];
                return obj;
            }, {});
        });
        // Refs
        const nativeSelect = ref();
        const selectTrigger = ref();
        const dropdown = ref();
        const searchInput = ref();
        const optionsContainer = ref();
        // State
        const isOpen = ref(false);
        const isFocused = ref(false);
        const searchQuery = ref("");
        const highlightedIndex = ref(-1);
        const dropdownAbove = ref(false);
        // Computed
        const hasSelection = computed(() => {
            if (props.multiple) {
                return Array.isArray(props.modelValue) && props.modelValue.length > 0;
            }
            return (props.modelValue !== undefined &&
                props.modelValue !== null &&
                props.modelValue !== "");
        });
        const displayValue = computed(() => {
            if (!hasSelection.value)
                return "";
            if (props.multiple && Array.isArray(props.modelValue)) {
                const selectedOptions = props.options.filter((option) => props.modelValue.includes(option.value));
                return selectedOptions.map((option) => option.label).join(", ");
            }
            else {
                const selectedOption = props.options.find((option) => option.value === props.modelValue);
                return (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || "";
            }
        });
        const filteredOptions = computed(() => {
            if (!props.searchable || !searchQuery.value) {
                return props.options;
            }
            const query = searchQuery.value.toLowerCase();
            return props.options.filter((option) => option.label.toLowerCase().includes(query));
        });
        // Methods
        const isSelected = (value) => {
            if (props.multiple && Array.isArray(props.modelValue)) {
                return props.modelValue.includes(value);
            }
            return props.modelValue === value;
        };
        const handleTriggerClick = () => {
            if (props.disabled)
                return;
            if (isOpen.value) {
                closeDropdown();
            }
            else {
                openDropdown();
            }
        };
        const openDropdown = () => __awaiter(this, void 0, void 0, function* () {
            if (props.disabled || isOpen.value)
                return;
            isOpen.value = true;
            emit("open");
            yield nextTick();
            // Position dropdown
            positionDropdown();
            // Focus search input if searchable
            if (props.searchable && searchInput.value) {
                searchInput.value.focus();
            }
            // Reset highlighted index
            highlightedIndex.value = -1;
        });
        const closeDropdown = () => {
            if (!isOpen.value)
                return;
            isOpen.value = false;
            searchQuery.value = "";
            highlightedIndex.value = -1;
            emit("close");
            // Return focus to trigger
            if (selectTrigger.value) {
                selectTrigger.value.focus();
            }
        };
        const positionDropdown = () => {
            if (!dropdown.value || !selectTrigger.value)
                return;
            const triggerRect = selectTrigger.value.getBoundingClientRect();
            const dropdownHeight = dropdown.value.offsetHeight;
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;
            dropdownAbove.value =
                spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
        };
        const handleSingleSelect = (value) => {
            emit("update:modelValue", value);
            closeDropdown();
            // Dispatch change event on native select
            if (nativeSelect.value) {
                nativeSelect.value.value = String(value);
                nativeSelect.value.dispatchEvent(new Event("change", { bubbles: true }));
            }
        };
        const handleMultipleChange = (newValue) => {
            emit("update:modelValue", newValue);
            // Update native select
            if (nativeSelect.value) {
                Array.from(nativeSelect.value.options).forEach((option) => {
                    option.selected = newValue.includes(option.value);
                });
                nativeSelect.value.dispatchEvent(new Event("change", { bubbles: true }));
            }
        };
        const clearSelection = () => {
            const emptyValue = props.multiple ? [] : "";
            emit("update:modelValue", emptyValue);
            emit("clear");
            // Update native select
            if (nativeSelect.value) {
                if (props.multiple) {
                    Array.from(nativeSelect.value.options).forEach((option) => {
                        option.selected = false;
                    });
                }
                else {
                    nativeSelect.value.value = "";
                }
                nativeSelect.value.dispatchEvent(new Event("change", { bubbles: true }));
            }
        };
        const handleOptionClick = (event) => {
            // Only prevent event bubbling, but allow default checkbox behavior
            event.stopPropagation();
        };
        // Native select event handlers
        const handleNativeChange = (event) => {
            emit("change", event);
        };
        const handleFocus = (event) => {
            // When native select gets focus (usually from label click), redirect to trigger
            if (selectTrigger.value && event.target === nativeSelect.value) {
                selectTrigger.value.focus();
                return;
            }
            isFocused.value = true;
            emit("focus", event);
        };
        const handleBlur = (event) => {
            isFocused.value = false;
            emit("blur", event);
        };
        const handleTriggerFocus = (event) => {
            isFocused.value = true;
            emit("focus", event);
        };
        const handleTriggerBlur = (event) => {
            // Delay blur to allow dropdown interactions
            setTimeout(() => {
                var _a;
                if (!((_a = dropdown.value) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))) {
                    isFocused.value = false;
                    emit("blur", event);
                    // For single select, also close dropdown
                    if (!props.multiple) {
                        closeDropdown();
                    }
                }
            }, 150);
        };
        // Keyboard navigation
        const handleKeydown = (event) => {
            switch (event.key) {
                case "Enter":
                case " ":
                    event.preventDefault();
                    if (isOpen.value) {
                        if (highlightedIndex.value >= 0 &&
                            filteredOptions.value[highlightedIndex.value]) {
                            const option = filteredOptions.value[highlightedIndex.value];
                            if (props.multiple) {
                                const currentValues = Array.isArray(props.modelValue)
                                    ? [...props.modelValue]
                                    : [];
                                const index = currentValues.indexOf(option.value);
                                if (index > -1) {
                                    currentValues.splice(index, 1);
                                }
                                else {
                                    currentValues.push(option.value);
                                }
                                handleMultipleChange(currentValues);
                            }
                            else {
                                handleSingleSelect(option.value);
                            }
                        }
                    }
                    else {
                        openDropdown();
                    }
                    break;
                case "Escape":
                    event.preventDefault();
                    closeDropdown();
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    if (!isOpen.value) {
                        openDropdown();
                    }
                    else {
                        highlightedIndex.value = Math.min(highlightedIndex.value + 1, filteredOptions.value.length - 1);
                        scrollToHighlighted();
                    }
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    if (isOpen.value) {
                        highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
                        scrollToHighlighted();
                    }
                    break;
                case "Tab":
                    if (isOpen.value) {
                        closeDropdown();
                    }
                    break;
            }
        };
        const handleSearchKeydown = (event) => {
            if (event.key === "Escape") {
                closeDropdown();
            }
            else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                handleKeydown(event);
            }
        };
        const scrollToHighlighted = () => {
            if (!optionsContainer.value || highlightedIndex.value < 0)
                return;
            const highlightedOption = optionsContainer.value.children[highlightedIndex.value];
            if (highlightedOption) {
                highlightedOption.scrollIntoView({ block: "nearest" });
            }
        };
        // Click outside handler
        const handleClickOutside = (event) => {
            var _a, _b;
            if (!((_a = selectTrigger.value) === null || _a === void 0 ? void 0 : _a.contains(event.target)) &&
                !((_b = dropdown.value) === null || _b === void 0 ? void 0 : _b.contains(event.target))) {
                closeDropdown();
            }
        };
        // Watchers
        watch(() => props.options, () => {
            // Reset search when options change
            searchQuery.value = "";
        });
        watch(searchQuery, (newQuery) => {
            emit("search", newQuery);
            highlightedIndex.value = -1;
        });
        // Lifecycle
        onMounted(() => {
            document.addEventListener("click", handleClickOutside);
        });
        onUnmounted(() => {
            document.removeEventListener("click", handleClickOutside);
        });
        const __returned__ = { props, emit, attrs, containerAttrs, selectAttrs, nativeSelect, selectTrigger, dropdown, searchInput, optionsContainer, isOpen, isFocused, searchQuery, highlightedIndex, dropdownAbove, hasSelection, displayValue, filteredOptions, isSelected, handleTriggerClick, openDropdown, closeDropdown, positionDropdown, handleSingleSelect, handleMultipleChange, clearSelection, handleOptionClick, handleNativeChange, handleFocus, handleBlur, handleTriggerFocus, handleTriggerBlur, handleKeydown, handleSearchKeydown, scrollToHighlighted, handleClickOutside, get SearchIcon() { return SearchIcon; }, Checkbox };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
